// src/components/video/exportUploadedVideo.js
//
// Records a "My video" (Video upload studio) to an MP4 in the browser:
// your uploaded video plays, every frame is drawn onto a canvas with the
// pictures and lyrics on top, and MediaRecorder captures the canvas plus the
// video's own sound — and, when switched on, the topic's song, started
// `songOffset` seconds into the video (minus = the song's intro is cut), the
// same rule the lyrics use, so the song and the lyrics stay together.
//
// It reuses the Sing-along exporter's format checks (findPlayableMime,
// checkPlayable) so it never saves a grey, unplayable file — but it does not
// call or change exportSingAlongVideo itself, so the Sing-along and Lesson
// video studios record exactly as before.
//
// REAL TIME: a 3-minute video takes 3 minutes, and the tab must stay visible
// the whole time (browsers stop drawing in hidden tabs).

import {
  SingAlongExportError,
  canExportVideo,
  checkPlayable,
  findPlayableMime,
  labelOf,
} from "../singalong/exportSingAlongVideo";

const loadImage = (url) =>
  new Promise((resolve) => {
    if (!url) return resolve(null);
    const img = new Image();
    img.crossOrigin = "anonymous";
    const timer = setTimeout(() => resolve(null), 8000);
    img.onload = () => {
      clearTimeout(timer);
      resolve(img);
    };
    img.onerror = () => {
      clearTimeout(timer);
      resolve(null);
    };
    img.src = url;
  });

export { canExportVideo };

/**
 * opts: {
 *   source,              a Blob/File of the video, OR
 *   sourceUrl,           its download URL (fetched with CORS)
 *   width = 1280, height = 720, fps = 30,
 *   audible = false,     hear it while it records
 *   logoUrl,             optional corner logo
 *   songUrl,             the topic's song to play with the video ("" = none)
 *   songOffset = 0,      video time where the song's 0:00 is
 *   songVolume = 1, videoVolume = 1
 *   renderFrame(ctx, { width, height, t, video, logo }),
 *   onProgress(fraction, seconds, total), onStatus(text), signal
 * }
 * Resolves { blob, mime, codec, codecLabel, ext, seconds, logoMissing }.
 */
export const exportUploadedVideo = async (opts) => {
  const {
    source,
    sourceUrl,
    width = 1280,
    height = 720,
    fps = 30,
    audible = false,
    logoUrl,
    songUrl = "",
    songOffset = 0,
    songVolume = 1,
    videoVolume = 1,
    renderFrame,
    onProgress,
    onStatus,
    signal,
  } = opts;

  if (!canExportVideo()) {
    throw new SingAlongExportError("unsupported", "This browser cannot record video. Use the latest Chrome or Edge on a computer.");
  }

  const mime = await findPlayableMime({ width, height, onStatus });
  if (signal?.aborted) throw new SingAlongExportError("aborted", "Recording stopped.");

  // 1. The video as a local file, so drawing it never taints the canvas.
  let blob = source || null;
  if (!blob) {
    if (onStatus) onStatus("Downloading your video…");
    try {
      const res = await fetch(sourceUrl, { mode: "cors" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      blob = await res.blob();
    } catch {
      throw new SingAlongExportError(
        "cors",
        "Your video could not be downloaded for recording. The storage bucket's CORS setting has to list this site."
      );
    }
  }
  if (onStatus) onStatus("");

  // 1b. The topic's song, also as a local file (Web Audio needs it readable)
  let songBlobUrl = "";
  if (songUrl) {
    if (onStatus) onStatus("Downloading the song…");
    try {
      const res = await fetch(songUrl, { mode: "cors" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      songBlobUrl = URL.createObjectURL(await res.blob());
    } catch {
      throw new SingAlongExportError("cors", "The topic's song could not be downloaded for recording. The storage bucket's CORS setting has to list this site.");
    }
    if (onStatus) onStatus("");
  }

  const logo = logoUrl ? await loadImage(logoUrl) : null;
  const logoMissing = Boolean(logoUrl) && !logo;

  const videoUrl = URL.createObjectURL(blob);
  const video = document.createElement("video");
  video.playsInline = true;
  video.preload = "auto";
  video.muted = false; // muted elements send silence into Web Audio
  video.src = videoUrl;
  // Off-screen but "rendered": some browsers stop decoding a video that is
  // not in the page at all.
  Object.assign(video.style, { position: "fixed", left: "-10px", top: "-10px", width: "2px", height: "2px", opacity: "0", pointerEvents: "none" });
  document.body.appendChild(video);

  await new Promise((resolve, reject) => {
    video.addEventListener("canplaythrough", () => resolve(), { once: true });
    video.addEventListener("error", () => reject(new SingAlongExportError("video", "Your video file could not be read by this browser.")), { once: true });
    video.load();
  });

  // 2. Sound: video -> recorder (and speakers if asked).
  const AudioCtx = window.AudioContext || window.webkitAudioContext;
  const actx = new AudioCtx();
  await actx.resume();
  const src = actx.createMediaElementSource(video);
  const dest = actx.createMediaStreamDestination();
  const videoGain = actx.createGain();
  videoGain.gain.value = Math.max(0, Math.min(1, Number(videoVolume)));
  src.connect(videoGain);
  videoGain.connect(dest);
  if (audible) videoGain.connect(actx.destination);

  // the song, locked to the video: song time = video time − songOffset
  let song = null;
  if (songBlobUrl) {
    song = document.createElement("audio");
    song.preload = "auto";
    song.src = songBlobUrl;
    await new Promise((resolve) => {
      song.addEventListener("canplaythrough", () => resolve(), { once: true });
      song.addEventListener("error", () => resolve(), { once: true });
      song.load();
    });
    const songSrc = actx.createMediaElementSource(song);
    const songGain = actx.createGain();
    songGain.gain.value = Math.max(0, Math.min(1, Number(songVolume)));
    songSrc.connect(songGain);
    songGain.connect(dest);
    if (audible) songGain.connect(actx.destination);
  }
  const syncSong = () => {
    if (!song) return;
    const st = video.currentTime - (Number(songOffset) || 0);
    const dur = Number.isFinite(song.duration) ? song.duration : Infinity;
    if (video.paused || st < 0 || st >= dur) {
      if (!song.paused) song.pause();
      return;
    }
    if (Math.abs(song.currentTime - st) > 0.3) song.currentTime = st;
    if (song.paused) song.play().catch(() => {});
  };

  // 3. Canvas -> video track.
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  const draw = () => renderFrame(ctx, { width, height, t: video.currentTime, video, logo });
  draw();

  const stream = canvas.captureStream(fps);
  dest.stream.getAudioTracks().forEach((track) => stream.addTrack(track));

  const recorder = new MediaRecorder(stream, {
    mimeType: mime,
    videoBitsPerSecond: width >= 1920 ? 8_000_000 : 5_000_000,
    audioBitsPerSecond: 192_000,
  });
  const chunks = [];
  recorder.ondataavailable = (e) => {
    if (e.data && e.data.size) chunks.push(e.data);
  };
  const finished = new Promise((resolve) => {
    recorder.onstop = () => resolve();
  });

  const total = Number.isFinite(video.duration) ? video.duration : 0;
  let raf = 0;
  let aborted = false;
  let stopping = false;

  const finish = (tailMs) => {
    if (stopping) return;
    stopping = true;
    setTimeout(() => {
      try {
        video.pause();
      } catch {
        /* already stopped */
      }
      if (recorder.state !== "inactive") recorder.stop();
    }, tailMs);
  };

  const loop = () => {
    draw();
    syncSong();
    if (onProgress && total) onProgress(Math.min(1, video.currentTime / total), video.currentTime, total);
    if (!video.ended && !aborted && !stopping) raf = requestAnimationFrame(loop);
  };

  if (signal) {
    signal.addEventListener(
      "abort",
      () => {
        aborted = true;
        if (recorder.state !== "inactive") recorder.stop();
      },
      { once: true }
    );
  }

  video.addEventListener(
    "ended",
    () => {
      draw();
      finish(400);
    },
    { once: true }
  );

  video.currentTime = 0;
  if (song && Number(songOffset) < 0) song.currentTime = -Number(songOffset);
  recorder.start(1000);
  try {
    await video.play();
    syncSong();
  } catch (err) {
    if (recorder.state !== "inactive") recorder.stop();
    throw new SingAlongExportError("video", `Your video would not play: ${err?.message || err}`);
  }
  raf = requestAnimationFrame(loop);

  await finished;
  cancelAnimationFrame(raf);
  try {
    video.pause();
  } catch {
    /* already stopped */
  }
  stream.getTracks().forEach((track) => track.stop());
  if (song) {
    song.pause();
    song.removeAttribute("src");
    URL.revokeObjectURL(songBlobUrl);
  }
  actx.close().catch(() => {});
  video.removeAttribute("src");
  video.load();
  video.remove();
  URL.revokeObjectURL(videoUrl);

  if (aborted) throw new SingAlongExportError("aborted", "Recording stopped.");

  const baseType = mime.split(";")[0];
  const out = new Blob(chunks, { type: baseType });

  if (onStatus) onStatus("Checking the finished video plays…");
  const check = await checkPlayable(out, 15000);
  if (onStatus) onStatus("");
  if (!check.ok) {
    throw new SingAlongExportError(
      "unplayable",
      `The recording came out as ${labelOf(recorder.mimeType || mime)}, but it won't play back here: ${check.why}. Nothing was saved. Press Make video again.`
    );
  }

  return {
    blob: out,
    mime: baseType,
    codec: recorder.mimeType || mime,
    codecLabel: labelOf(recorder.mimeType || mime),
    ext: baseType === "video/mp4" ? "mp4" : "webm",
    seconds: total,
    logoMissing,
  };
};

export default exportUploadedVideo;
